import React from "react";
import { StatusText, Typography } from "../../../../../../../../shared/frontend/ui";
import type { ListColumn } from "../../../../../../../../shared/frontend/list/listColumn";
import type { StudioWithAccess } from "../../../../app/api/types";
import { formatCheckInEventMessage } from "../../../../app/checkin/checkInEventMessage";
import { formatCheckInReader } from "../../../../app/checkin/checkInReader";
import { formatCheckInSource } from "../../../../app/checkin/checkInSource";
import type { LocalizedText } from "../../../../app/i18n/i18n";
import type { useI18n } from "../../../../app/i18n/useI18n";
import { formatDateOnly, formatDateTimeIso, formatMoney } from "../../../../app/units/format";
import type { UnitsSettings } from "../../../../app/units/units";
import { paymentMethodLabel } from "../../../../app/utils/paymentMethod";
import type { ColumnDefinition } from "../../../../app/utils/useProTableColumnEditor";
import { SettingsListPage, type ProColumnsWithMeasure } from "../../../../components/SettingsListPage";
import { paymentReferenceDisplayValue } from "../paymentReferenceDisplay";
import { formatAttendanceDuration, type AttendanceSessionRow } from "../personCheckInData";
import type { PaymentRow } from "../personDetailTypes";
import { personPaymentDescription } from "../personPaymentDisplay";
import { PERSON_OVERVIEW_TABLE_PAGE_SIZE, PERSON_OVERVIEW_TABLE_PAGE_SIZE_OPTIONS } from "./personOverviewWidgets";
import { ISO_DATE_LENGTH } from "../../../../app/utils/isoDate";
import { PAYMENT_ROW_KIND_DEBT_COLLECTION } from "../personDetailValues";

const PERSON_OVERVIEW_TABLES_COLUMN_WIDTH = {
  paidDate: 140,
  amount: 140,
  description: 200,
  method: 200,
  checkInTime: 180,
  checkOutTime: 180,
  duration: 120,
  studio: 180,
  status: 120,
  source: 150,
  medium: 160,
  reader: 190
} as const;

/**
 * Tabellen der Übersicht (Check-in-Sitzungen, letzte Zahlungen) mit den bisherigen Spalten, Spalten-Editor und
 * umstellbarer Seitengröße. Sie stehen als Inhalt in den Karten des Dashboard-Rasters (`DashboardGrid`) und
 * sind deshalb eingebettet — der Kartenrahmen kommt vom Raster (`check-settings-list-frames.mjs` führt sie
 * als rahmengebundene Adapter).
 */

const RECENT_PAYMENTS_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: "paidDate", label: "Date", defaultVisible: true },
  { key: "amount", label: "Amount", defaultVisible: true },
  { key: "description", label: "For", defaultVisible: true },
  { key: "method", label: "Payment method", defaultVisible: true },
  { key: "reference", label: "Reference", defaultVisible: true },
];


function createRecentPaymentsColumns(
  text: (key: LocalizedText) => string,
  units: UnitsSettings,
  paymentMethodNameByType: Record<number, string>
): Record<string, ProColumnsWithMeasure<PaymentRow>> {
  return {
    paidDate: {
      title: text("Date"),
      dataIndex: "paidDate",
      key: "paidDate",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.paidDate,
      defaultSortOrder: "descend",
      sorter: (a, b) => {
        const aDate = String(a.paidDate || "").slice(0, ISO_DATE_LENGTH);
        const bDate = String(b.paidDate || "").slice(0, ISO_DATE_LENGTH);
        const paidCompare = aDate.localeCompare(bDate);
        if (paidCompare !== 0) return paidCompare;
        return String(a.bookedAt || "").localeCompare(String(b.bookedAt || ""));
      },
      render: (_, r) => {
        const dateStr = String(r.paidDate || "").slice(0, ISO_DATE_LENGTH);
        return dateStr ? formatDateOnly(dateStr, units) : "—";
      }
    },
    amount: {
      title: text("Amount"),
      dataIndex: "amount",
      key: "amount",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.amount,
      align: "right",
      sorter: (a, b) => Number(a.amount || 0) - Number(b.amount || 0),
      render: (_, r) => formatMoney(Number(r.amount || 0), units)
    },
    description: {
      title: text("Description"),
      key: "description",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.description,
      sortValue: (r) => personPaymentDescription(r, text),
      render: (_, r) => {
        const desc = personPaymentDescription(r, text);
        if (desc) return <Typography.Text>{desc}</Typography.Text>;
        return <Typography.Text type="secondary">–</Typography.Text>;
      }
    },
    method: {
      title: text("Payment method"),
      dataIndex: "method",
      key: "method",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.method,
      sorter: (a, b) => {
        const aLabel =
          a.rowKind === PAYMENT_ROW_KIND_DEBT_COLLECTION && a.debtCollectionAgencyName
            ? text("Debt collection with agency").replace("{0}", String(a.debtCollectionAgencyName))
            : paymentMethodLabel(a.method, paymentMethodNameByType);
        const bLabel =
          b.rowKind === PAYMENT_ROW_KIND_DEBT_COLLECTION && b.debtCollectionAgencyName
            ? text("Debt collection with agency").replace("{0}", String(b.debtCollectionAgencyName))
            : paymentMethodLabel(b.method, paymentMethodNameByType);
        return aLabel.localeCompare(bLabel);
      },
      render: (_, r) => {
        if (r.rowKind === PAYMENT_ROW_KIND_DEBT_COLLECTION && r.debtCollectionAgencyName) {
          return text("Debt collection with agency").replace("{0}", String(r.debtCollectionAgencyName));
        }
        return paymentMethodLabel(r.method, paymentMethodNameByType);
      }
    },
    reference: {
      title: text("Reference"),
      dataIndex: "reference",
      key: "reference",
      ellipsis: false,
      render: (_, payment) => paymentReferenceDisplayValue(payment.reference) || "—"
    },
  };
}

type Text = ReturnType<typeof useI18n>["text"];

export function PersonRecentPaymentsTable({ payments, loading, error, accountId, text, units, paymentMethodNameByType }: {
  payments: PaymentRow[];
  loading: boolean;
  error: boolean;
  accountId?: string | null;
  text: Text;
  units: UnitsSettings;
  paymentMethodNameByType: Record<number, string>;
}) {
  const allColumnsMap = React.useMemo(
    () => createRecentPaymentsColumns(text, units, paymentMethodNameByType),
    [text, units, paymentMethodNameByType]
  );
  return (
    <SettingsListPage<PaymentRow>
      embedded
      storageKey="persons.detail.overview.payments"
      // Bestehender Spaltenschlüssel je Konto bleibt erhalten.
      columnEditorStorageKey={`persons-detail-overview-payments-columns-${accountId}`}
      rowKey="id"
      dataSource={error ? [] : payments}
      loading={loading}
      columnDefinitions={RECENT_PAYMENTS_COLUMN_DEFINITIONS}
      allColumnsMap={allColumnsMap}
      emptyText={error ? <Typography.Text type="danger">{text("Error loading data")}</Typography.Text> : text("No payments")}
      pagination={{ pageSize: PERSON_OVERVIEW_TABLE_PAGE_SIZE, pageSizeOptions: PERSON_OVERVIEW_TABLE_PAGE_SIZE_OPTIONS }}
    />
  );
}

export function PersonCheckInSessionsTable({ sessions, loading, allowedStudios, text, units }: {
  sessions: AttendanceSessionRow[];
  loading: boolean;
  allowedStudios: StudioWithAccess[];
  text: Text;
  units: UnitsSettings;
}) {
  const checkinsColumnDefinitions: ColumnDefinition[] = React.useMemo(
    () => [
      { key: "checkInTime", label: "From", defaultVisible: true },
      { key: "checkOutTime", label: "Until", defaultVisible: true },
      { key: "duration", label: "Duration", defaultVisible: true },
      { key: "studio", label: "Studio", defaultVisible: true },
      { key: "source", label: "Source", defaultVisible: true },
      { key: "medium", label: "Medium", defaultVisible: true },
      { key: "reader", label: "Reader", defaultVisible: true },
      { key: "status", label: "Status", defaultVisible: true },
      { key: "message", label: "Message", defaultVisible: false }
    ],
    []
  );

  const checkinsAllColumnsMap: Record<string, ListColumn<AttendanceSessionRow>> = React.useMemo(() => ({
    checkInTime: {
      title: text("From"),
      dataIndex: "checkInTime",
      key: "checkInTime",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.checkInTime,
      sorter: (a, b) => {
        const aTime = a.checkInTime ? new Date(a.checkInTime).getTime() : 0;
        const bTime = b.checkInTime ? new Date(b.checkInTime).getTime() : 0;
        return aTime - bTime;
      },
      render: (_: unknown, r: AttendanceSessionRow) => {
        if (!r.checkInTime) return "—";
        return formatDateTimeIso(String(r.checkInTime), units, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      }
    },
    checkOutTime: {
      title: text("Until"),
      dataIndex: "checkOutTime",
      key: "checkOutTime",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.checkOutTime,
      sorter: (a, b) => {
        const aTime = a.checkOutTime ? new Date(a.checkOutTime).getTime() : 0;
        const bTime = b.checkOutTime ? new Date(b.checkOutTime).getTime() : 0;
        return aTime - bTime;
      },
      render: (_: unknown, r: AttendanceSessionRow) => {
        if (!r.checkOutTime) return "—";
        return formatDateTimeIso(String(r.checkOutTime), units, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
      }
    },
    duration: {
      title: text("Duration"),
      dataIndex: "duration",
      key: "duration",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.duration,
      align: "right",
      sorter: (a, b) => (a.duration || 0) - (b.duration || 0),
      render: (_: unknown, r: AttendanceSessionRow) => {
        if (r.duration == null) return "—";
        return formatAttendanceDuration(r.duration);
      }
    },
    studio: {
      title: text("Studio"),
      dataIndex: "studioId",
      key: "studio",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.studio,
      sorter: (a, b) => {
        const aStudio = allowedStudios.find((s) => s.id === a.studioId)?.name || "";
        const bStudio = allowedStudios.find((s) => s.id === b.studioId)?.name || "";
        return aStudio.localeCompare(bStudio);
      },
      render: (_: unknown, r: AttendanceSessionRow) => {
        if (!r.studioId) return "—";
        const studio = allowedStudios.find((s) => s.id === r.studioId);
        return studio?.name || r.studioId;
      }
    },
    status: {
      title: text("Status"),
      dataIndex: "allowed",
      key: "status",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.status,
      sorter: (a, b) => (a.allowed ? 1 : 0) - (b.allowed ? 1 : 0),
      // Nur die Abweichung wird markiert (DNA D29): erlaubte Sitzungen bleiben leer.
      render: (_: unknown, r: AttendanceSessionRow) => (r.allowed ? null : <StatusText color="error">{text("Denied")}</StatusText>)
    },
    source: {
      title: text("Source"),
      dataIndex: "source",
      key: "source",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.source,
      render: (_: unknown, r: AttendanceSessionRow) => formatCheckInSource(r.source, text)
    },
    medium: {
      title: text("Medium"),
      dataIndex: "accessMedium",
      key: "medium",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.medium,
      ellipsis: true,
      render: (_: unknown, r: AttendanceSessionRow) => r.accessMedium || "—"
    },
    reader: {
      title: text("Reader"),
      key: "reader",
      width: PERSON_OVERVIEW_TABLES_COLUMN_WIDTH.reader,
      ellipsis: true,
      sortValue: (r: AttendanceSessionRow) => formatCheckInReader(r),
      render: (_: unknown, r: AttendanceSessionRow) => formatCheckInReader(r)
    },
    message: {
      title: text("Message"),
      dataIndex: "message",
      key: "message",
      ellipsis: true,
      render: (_: unknown, r: AttendanceSessionRow) => formatCheckInEventMessage(r.message, text)
    }
  }), [allowedStudios, text, units]);

  return (
    <SettingsListPage<AttendanceSessionRow>
      embedded
      storageKey="persons.detail.checkins"
      rowKey="id"
      dataSource={sessions}
      loading={loading}
      columnDefinitions={checkinsColumnDefinitions}
      allColumnsMap={checkinsAllColumnsMap as Record<string, ProColumnsWithMeasure<AttendanceSessionRow>>}
      emptyText={text("Person overview no check-ins")}
      pagination={{ pageSize: PERSON_OVERVIEW_TABLE_PAGE_SIZE, pageSizeOptions: PERSON_OVERVIEW_TABLE_PAGE_SIZE_OPTIONS }}
    />
  );
}
